import { Box } from "@mui/material";
import { Theme, alpha, useTheme } from "@mui/material/styles";
import React, { useMemo } from "react";

import { monospaceStyle } from "../../../theme";
import { TreemapColour } from "../analyticsState";
import {
  FRAME_STRIP,
  TreemapRect,
  countFits,
  labelFits,
  tintOf,
} from "./treemapLayout";
import { counted, rectName, rectTitle } from "./treemapModel";

// The opacity of each of the five tints, from the faintest to the strongest.
// The faintest of the accent is no tint at all: the plain raised tile.
export const TINT_OPACITY: readonly number[] = [0, 0.14, 0.28, 0.42, 0.6];
// A domestic share of nothing is still a share: its scale starts tinted
export const DOMESTIC_OPACITY: readonly number[] = [0.14, 0.28, 0.42, 0.6, 0.8];

// The fill of a tint under a colour mode; a null tint is no value to colour
// by, the plain raised tile
export const tintFill = (theme: Theme, mode: TreemapColour, tint: number | null): string => {
  if (tint === null) {
    return theme.palette.background.raised;
  }
  if (mode === "domestic") {
    return alpha(theme.palette.chart.home, DOMESTIC_OPACITY[tint]);
  }
  return tint === 0
    ? theme.palette.background.raised
    : alpha(theme.palette.accent.main, TINT_OPACITY[tint]);
};

// The hatching of a tile that is no finding: thin stripes of the two surfaces
export const hatchOf = (theme: Theme): string => {
  const { paper, raised } = theme.palette.background;
  return `repeating-linear-gradient(45deg, ${paper}, ${paper} 3px, ${raised} 3px, ${raised} 6px)`;
};

// The fill of a frame: one tone under the canvas
export const frameFill = (theme: Theme): string =>
  alpha(theme.palette.chart.shadow, 0.14);

// A tile is hatched where its number is no finding: a gap, a class under the
// support floor, or the products no class places. A frame never is.
export const isHatched = (rect: TreemapRect): boolean =>
  !rect.inner && (rect.gap || rect.thin || rect.other);

// The tint of a rectangle under a colour mode, 0 to 4; null where the mode
// has no value to colour by (a domestic share without one)
export const tintFor = (rect: TreemapRect, mode: TreemapColour): number | null => {
  const scaled = mode === "domestic" ? rect.colour.domestic : rect.colour[mode];
  return scaled === null ? null : tintOf(scaled);
};

// Below this a tile takes the tight padding
const ROOMY_MIN_WIDTH = 96;
const ROOMY_MIN_HEIGHT = 52;
// From this width a tile names its makers beside its count
const MAKERS_MIN_WIDTH = 150;
const LABEL_LINE = 16;
const COUNT_LINE = 15;

interface TreemapCanvasProps {
  rects: TreemapRect[];
  width: number;
  height: number;
  colour: TreemapColour;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onDrill: (nodeId: string) => void;
  onOpen: (nodeId: string) => void;
}

// The rectangles of the layout, drawn flat in depth order so that a frame is
// under what it holds: a frame is a header band over its tiles, a tile a
// filled box with its name and its count. A click drills into a class with
// classes beneath it and opens one with none; a tile that is no class of its
// own is selected, to be read in the panel.
const TreemapCanvas: React.FC<TreemapCanvasProps> = ({
  rects,
  width,
  height,
  colour,
  selectedKey,
  onSelect,
  onDrill,
  onOpen,
}) => {
  const theme = useTheme();
  const accent = theme.palette.accent.main;
  const hatch = hatchOf(theme);
  const labels = useMemo(
    () => new Map(rects.map((rect) => [rect.key, rect.label])),
    [rects],
  );
  const drawn = useMemo(
    () => [...rects].sort((a, b) => Number(!a.inner) - Number(!b.inner) || a.depth - b.depth),
    [rects],
  );
  // A class with classes beneath it is drilled into; one with none is
  // opened. The products filed on a class itself and those no class places
  // are no class to open: they are read in the panel.
  const activate = (rect: TreemapRect) => {
    if (rect.drillable) {
      onDrill(rect.nodeId);
    } else if (rect.own || rect.other) {
      onSelect(rect.key);
    } else {
      onOpen(rect.nodeId);
    }
  };

  return (
    <Box
      role="group"
      aria-label="Treemap"
      // Pixels go through `style`: `sx` reads a width of 1 or less as a
      // share of the container
      style={{ width, height }}
      sx={{ position: "relative", flexShrink: 0, overflow: "hidden" }}
    >
      {drawn.map((rect) => {
        const selected = rect.key === selectedKey;
        const parentLabel = rect.own ? (labels.get(rect.nodeId) ?? null) : null;
        const tint = rect.inner || rect.gap ? null : tintFor(rect, colour);
        const hatched = isHatched(rect);
        const roomy = rect.width >= ROOMY_MIN_WIDTH && rect.height >= ROOMY_MIN_HEIGHT;
        const padY = roomy ? 8 : 4;
        const showLabel = labelFits(rect);
        const showCount = countFits(rect);
        const lines = Math.max(
          1,
          Math.min(
            3,
            Math.floor((rect.height - 2 * padY - (showCount ? COUNT_LINE : 0)) / LABEL_LINE),
          ),
        );
        return (
          <Box
            key={rect.key}
            role="button"
            tabIndex={0}
            data-testid="tm-rect"
            data-key={rect.key}
            data-inner={rect.inner}
            data-own={rect.own}
            data-gap={rect.gap}
            data-thin={rect.thin}
            data-other={rect.other}
            data-depth={rect.depth}
            data-hatched={hatched}
            data-tint={tint === null ? "none" : `s${tint}`}
            data-selected={selected}
            aria-label={rectName(rect, parentLabel)}
            aria-pressed={selected}
            title={rectTitle(rect, parentLabel)}
            data-drillable={rect.drillable}
            onClick={(event: React.MouseEvent) => {
              // The second click of a double click lands on whatever the
              // first one drew there: it is not a click on that
              if (event.detail <= 1) {
                activate(rect);
              }
            }}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                activate(rect);
              }
            }}
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.width,
              height: rect.height,
              // A frame inside a frame keeps the tone of the one around it
              backgroundColor: rect.inner
                ? rect.depth === 0
                  ? frameFill(theme)
                  : "transparent"
                : hatched
                  ? undefined
                  : tintFill(theme, colour, tint),
            }}
            sx={{
              backgroundImage: hatched ? hatch : "none",
              position: "absolute",
              boxSizing: "border-box",
              overflow: "hidden",
              cursor: rect.drillable ? "zoom-in" : "pointer",
              border: "1px solid",
              borderStyle: rect.gap || rect.own ? "dashed" : "solid",
              borderColor:
                rect.gap || rect.own ? alpha(theme.palette.chart.ink, 0.16) : "divider",
              borderRadius: `${
                rect.inner && rect.depth === 0 ? theme.radius.surface : theme.radius.chip
              }px`,
              boxShadow: selected ? `0 0 0 3px ${alpha(accent, 0.35)}` : "none",
              zIndex: selected ? 1 : "auto",
              color: hatched ? "text.secondary" : "text.primary",
              transition: "border-color 120ms",
              "&:hover": { borderColor: alpha(theme.palette.chart.ink, 0.22) },
              "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 1 },
              ...(rect.inner
                ? {}
                : {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: roomy ? "8px 10px" : "4px 6px",
                  }),
            }}
          >
            {rect.inner
              ? showLabel && (
                  // The header band: the tiles start under it
                  <Box
                    style={{ height: FRAME_STRIP }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: "8px",
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {rect.label}
                    </Box>
                    {showCount && (
                      <Box component="span" sx={{ flexShrink: 0, ...monospaceStyle }}>
                        &nbsp;·&nbsp;{rect.count}
                      </Box>
                    )}
                  </Box>
                )
              : showLabel && (
                  <>
                    <Box
                      sx={{
                        fontSize: roomy ? 12.5 : 11.5,
                        fontWeight: 500,
                        lineHeight: `${LABEL_LINE}px`,
                        fontStyle: rect.own ? "italic" : "normal",
                        overflow: "hidden",
                        overflowWrap: "anywhere",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: lines,
                      }}
                    >
                      {rect.label}
                    </Box>
                    {showCount && (
                      <Box
                        sx={{
                          ...monospaceStyle,
                          fontSize: 11,
                          lineHeight: `${COUNT_LINE}px`,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          color: tint !== null && tint >= 3 && !hatched ? "text.primary" : "text.secondary",
                        }}
                      >
                        {rect.gap
                          ? "none"
                          : rect.width >= MAKERS_MIN_WIDTH && rect.makers > 0
                            ? `${rect.count} · ${counted(rect.makers, "maker")}`
                            : rect.count}
                      </Box>
                    )}
                  </>
                )}
          </Box>
        );
      })}
    </Box>
  );
};

export default TreemapCanvas;
